import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Topic } from '../../types';
import { SettingsCard } from './SettingsCard';
import { useTrackerAiPath } from '../../utils/paths';

interface TopicRow extends Topic {
  promptCount?: number;
}

export function SettingsTopicsSection({
  topics,
  loading,
  onNewTopic,
  onRename,
  renamingTopicId,
}: {
  topics: TopicRow[];
  loading?: boolean;
  onNewTopic: () => void;
  onRename: (topicId: string, name: string) => Promise<void>;
  renamingTopicId: string | null;
}) {
  const toTrackerAi = useTrackerAiPath();
  return (
    <SettingsCard
      title="تاپیک‌ها"
      helpKey="topics_settings"
      description="حوزه‌های تحقیقاتی که در این پروژه ردیابی می‌شوند."
      action={
        topics.length > 0 ? (
          <Link to={toTrackerAi('topics')} className="fs--1 fw-medium text-primary text-decoration-none">
            مدیریت تاپیک‌ها ←
          </Link>
        ) : undefined
      }
    >
      {loading ? (
        <p className="fs--1 text-600 mb-0">درحال بارگذاری</p>
      ) : topics.length === 0 ? (
        <div className="rounded-3 border border-dashed px-4 py-5 text-center">
          <p className="fs--1 text-600 mb-2">هنوز تاپیکی وجود ندارد.</p>
          <Button type="button" variant="link" className="fs--1 fw-medium text-decoration-none p-0" onClick={onNewTopic}>
            ایجاد اولین تاپیک
          </Button>
        </div>
      ) : (
        <ul className="list-unstyled mb-0">
          {topics.map(topic => (
            <TopicRowItem key={topic.id} topic={topic} renaming={renamingTopicId === topic.id} onRename={onRename} />
          ))}
        </ul>
      )}
    </SettingsCard>
  );
}

function TopicRowItem({
  topic,
  renaming,
  onRename,
}: {
  topic: TopicRow;
  renaming: boolean;
  onRename: (topicId: string, name: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(topic.name);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      setName(topic.name);
    }
  }, [topic.name, editing]);

  function cancelEdit() {
    setName(topic.name);
    setEditing(false);
  }

  async function saveEdit() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === topic.name) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onRename(topic.id, trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <li className="d-flex align-items-center gap-2 py-2 border-bottom">
        <Form.Control
          size="sm"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={saving || renaming}
          autoFocus
          onKeyDown={e => {
            if (e.key === 'Enter') void saveEdit();
            if (e.key === 'Escape') cancelEdit();
          }}
        />
        <Button type="button" size="sm" variant="primary" disabled={saving || renaming} onClick={() => void saveEdit()}>
          ذخیره
        </Button>
        <Button type="button" size="sm" variant="outline-secondary" disabled={saving || renaming} onClick={cancelEdit}>
          انصراف
        </Button>
        <span className="flex-shrink-0 badge badge-subtle-secondary d-none d-sm-inline">
          {topic.promptCount ?? 0} پرامپت
        </span>
      </li>
    );
  }

  return (
    <li className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
      <div className="d-flex min-w-0 flex-grow-1 align-items-center gap-2">
        <p className="min-w-0 text-truncate fw-medium text-1000 mb-0">{topic.name}</p>
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={() => setEditing(true)}
          className="flex-shrink-0 text-500 p-1"
          aria-label={`ویرایش ${topic.name}`}
          title="ویرایش نام تاپیک"
        >
          <FontAwesomeIcon icon="pen" className="fs--2" />
        </Button>
      </div>
      <span className="flex-shrink-0 badge badge-subtle-secondary">{topic.promptCount ?? 0} پرامپت</span>
    </li>
  );
}
